import { Autocomplete, Box, ListItem, TextField } from '@mui/material'
import { FC, useEffect, useState, useCallback } from 'react'
import { useDebounce } from 'use-debounce'
import { Controller, useFormContext } from 'react-hook-form'
import {
  useFetchAllUsersLazyQuery,
  useSearchUserLazyQuery,
  User,
  Maybe,
  useFindOneUserQuery,
  UserRole
} from '../../../generated'
import { DEFAULT_USER_ROLES, GRAPHQL_QUERY_POLICY, GRAPHQL_QUERY_POLICY_CACHE_FIRST, PAGE_LIMIT } from '../../constants'
import { UserDropdownWithFormProps } from '../../interfaceTypes/props'
import { assigneeDropdownStyle, flexCenter } from '../../theme/styleConstants'
import { badgeBgColor, capitalizeCase } from '../../utils'

const UserDropdownWithForm: FC<UserDropdownWithFormProps> = ({
  name,
  label = 'Attorney',
  placeholder,
  required = false,
  disabled = false,
  sx = { minWidth: 150 },
  roles = DEFAULT_USER_ROLES,
  rules = {},
  disableClearable = false,
  setRate = false,
  onUserResolved,
  onUserChange
}) => {
  const { control, watch, setValue } = useFormContext()
  const [attorneys, setAttorneys] = useState<Maybe<User>[]>([])
  const [attorneyPage, setAttorneyPage] = useState<number>(1)
  const [attorneySearchTerm, setAttorneySearchTerm] = useState<string>('')
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)
  const [inputValue, setInputValue] = useState<string>('')
  const [foundUser, setFoundUser] = useState<Maybe<User> | null>(null)
  const [debouncedAttorneySearchTerm] = useDebounce(attorneySearchTerm, 800)
  const [open, setOpen] = useState(false)

  const [fetchAttorneys, { loading: attorneyLoading }] = useFetchAllUsersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    // fetchPolicy: 'no-cache',
    // nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchAllUsers?.users) {
        const newUsers = data.fetchAllUsers.users || []
        setAttorneys(prev => {
          if (attorneyPage === 1) return newUsers
          const existingIds = new Set(prev.map(user => user?.id))
          const uniqueNewUsers = newUsers.filter(user => !existingIds.has(user?.id))
          return [...prev, ...uniqueNewUsers]
        })
        setHasMorePages((data.fetchAllUsers.pagination?.totalPages || 1) >= attorneyPage)
      }
    }
  })

  const selectedUserId = watch(name) ?? ''

  // Resolve the selected user locally wherever possible: picking an option hands
  // us the whole object, and the loaded page of options often already contains
  // it. Clear the display when the form value is cleared or reset externally.
  useEffect(() => {
    if (!selectedUserId) {
      setFoundUser(null)
      return
    }
    if (foundUser?.id === selectedUserId) return
    const alreadyLoaded = attorneys.find(user => user?.id === selectedUserId)
    if (alreadyLoaded) setFoundUser(alreadyLoaded)
  }, [selectedUserId, attorneys, foundUser])

  // Only look a user up when we hold an id we could NOT resolve locally — i.e. a
  // form pre-filled in edit mode before the options have loaded. Selecting from
  // the dropdown resolves locally, so it no longer triggers this query.
  useFindOneUserQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    skip: !selectedUserId || foundUser?.id === selectedUserId,
    variables: {
      findOneUserId: selectedUserId
    },
    onCompleted: data => {
      const user = data?.findOneUser?.user
      if (!user) return

      const foundRole = user.roles?.find(role => role?.role)
      if (roles.includes(foundRole?.role as UserRole)) {
        setFoundUser(user as User)
        return
      }

      // This dropdown cannot represent that user — their role is not one it
      // offers — so the field renders blank. Drop the form value as well, or
      // the two disagree: the entry forms default `userId` to whoever is logged
      // in, and a super-admin is not a timekeeper for a time entry. That left a
      // userId sitting invisibly in the form, which made the Matter dropdown
      // enable itself (and query for that user's matters) while the User field
      // still looked empty.
      setFoundUser(null)
      setValue(name, null)
    }
  })

  // Single place that reports the resolved user upward, so every path that can
  // set it (local pick, loaded option, lookup) is covered exactly once.
  useEffect(() => {
    onUserResolved?.(foundUser)
  }, [foundUser, onUserResolved])

  const [searchAttorneys, { loading: attorneySearchLoading }] = useSearchUserLazyQuery({
    // ...GRAPHQL_QUERY_POLICY,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.searchUser?.users) {
        setAttorneys(data.searchUser.users || [])
      }
    }
  })

  // Fetch paginated users when open and not searching
  useEffect(() => {
    if (open && !debouncedAttorneySearchTerm && hasMorePages) {
      fetchAttorneys({
        variables: {
          userInput: {
            paginationOptions: { page: attorneyPage, limit: PAGE_LIMIT },
            roles
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attorneyPage, debouncedAttorneySearchTerm, fetchAttorneys, roles, hasMorePages])

  // Search users when open and search term is present
  useEffect(() => {
    if (open && debouncedAttorneySearchTerm) {
      setAttorneyPage(1)
      setHasMorePages(true)
      searchAttorneys({
        variables: {
          searchUserInput: {
            roles,
            searchTerm: debouncedAttorneySearchTerm.trim()
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedAttorneySearchTerm, searchAttorneys, roles])

  // Clear data when closed
  useEffect(() => {
    if (!open) {
      setAttorneys([])
      setAttorneyPage(1)
      setHasMorePages(true)
      setAttorneySearchTerm('')
    }
  }, [open])

  const handleInputChange = useCallback((event: React.SyntheticEvent, newInputValue: string, reason: string) => {
    setInputValue(newInputValue)
    if (reason === 'input') {
      setAttorneySearchTerm(newInputValue)
    } else if (reason === 'clear' || reason === 'reset') {
      setAttorneySearchTerm('')
      setAttorneys([])
    }
  }, [])

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: Maybe<User> | null, reason: string) => {
      if (reason === 'selectOption' && newValue) {
        const fullName = `${newValue.firstName || ''} ${newValue.lastName || ''}`.trim()
        setInputValue(fullName)
        setAttorneySearchTerm('')
        // The option already IS the full user — record it so the lookup below
        // stays skipped instead of re-fetching what we were just handed.
        setFoundUser(newValue)
        if (setRate) {
          setValue('rate', newValue.hourlyRate)
        }
      } else if (reason === 'clear') {
        setInputValue('')
        setAttorneySearchTerm('')
        setFoundUser(null)
      }
      return newValue?.id
    },
    [setRate, setValue]
  )

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
        !debouncedAttorneySearchTerm &&
        hasMorePages &&
        !attorneyLoading
      ) {
        setAttorneyPage(prev => prev + 1)
      }
    },
    [debouncedAttorneySearchTerm, hasMorePages, attorneyLoading]
  )

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          sx={sx}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={foundUser}
          options={attorneys}
          inputValue={inputValue}
          onChange={(event, newValue, reason) => {
            const processedValue = handleChange(event, newValue, reason)
            field.onChange(processedValue || null)
            // Gated on the reason on purpose: this must fire for a deliberate
            // pick/clear only, never for a form reset restoring a saved entry.
            if (reason === 'selectOption' || reason === 'clear') onUserChange?.(newValue)
          }}
          onInputChange={handleInputChange}
          getOptionLabel={option =>
            `${capitalizeCase(option?.firstName || '')} ${capitalizeCase(option?.lastName || '')}`.trim()
          }
          loading={attorneyLoading || attorneySearchLoading}
          isOptionEqualToValue={(option, val) => option?.id === val?.id}
          disabled={disabled}
          filterOptions={x => x}
          disableClearable={disableClearable}
          renderOption={(props, option) => {
            const role = option?.roles?.[0]?.role || ''
            const roleColor = role === UserRole.Admin ? role : role.slice(0, 1)

            const fullName = `${capitalizeCase(option?.firstName || '')} ${capitalizeCase(
              option?.lastName || ''
            )}`.trim()

            return (
              <ListItem {...props} key={option?.id}>
                <Box position="relative">
                  {fullName}
                  <Box
                    component="span"
                    sx={{ ...assigneeDropdownStyle, ...flexCenter, background: badgeBgColor(roleColor) }}
                  >
                    {role.slice(0, 1)}
                  </Box>
                </Box>
              </ListItem>
            )
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              required={required}
              error={!!error}
              helperText={error?.message}
            />
          )}
          ListboxProps={{
            onScroll: handleScroll
          }}
        />
      )}
    />
  )
}

export default UserDropdownWithForm
